import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { api, errorMessage } from "../../lib/api";
import { Markdown } from "../../lib/markdown";

const CATEGORY_LABELS = {
    "getting-started-members": "Getting started — Members",
    "getting-started-advertisers": "Getting started — Advertisers",
    "payments-and-cashout": "Payments & cashout",
    "trust-and-safety": "Trust & safety",
    "privacy-and-data": "Privacy & data",
    "troubleshooting": "Troubleshooting",
};

export default function HelpArticle() {
    const { slug } = useParams();
    const [art, setArt] = useState(null);
    const [err, setErr] = useState(null);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        setArt(null);
        setErr(null);
        api.get(`/kb/articles/${slug}`)
            .then((r) => {
                setArt(r.data);
                return api.get(`/kb/articles?category=${r.data.category}`);
            })
            .then((r) => {
                if (r) setRelated((r.data || []).filter((x) => x.slug !== slug).slice(0, 4));
            })
            .catch((e) => setErr(errorMessage(e)));
    }, [slug]);

    if (err) {
        return (
            <div className="bg-paper min-h-screen">
                <Navbar />
                <div className="max-w-3xl mx-auto px-6 py-20" data-testid="help-article-error">
                    <h1 className="h1 mb-3">Article not found</h1>
                    <p className="text-ink-600 mb-6">{err}</p>
                    <Link to="/help" className="btn btn-primary" data-testid="help-back-to-index">Back to Help Center</Link>
                </div>
                <Footer />
            </div>
        );
    }

    if (!art) {
        return (
            <div className="bg-paper min-h-screen">
                <Navbar />
                <p className="text-ink-600 font-mono text-sm max-w-3xl mx-auto px-6 py-20">Loading article…</p>
                <Footer />
            </div>
        );
    }

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <article className="max-w-3xl mx-auto px-6 py-16" data-testid={`help-article-${art.slug}`}>
                <div className="font-mono text-[11px] text-ink-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Link to="/help" className="underline" data-testid="help-breadcrumb-root">Help</Link>
                    <span>·</span>
                    <Link to={`/help?category=${art.category}`} className="underline" data-testid="help-breadcrumb-category">
                        {CATEGORY_LABELS[art.category] || art.category}
                    </Link>
                </div>
                <h1 className="h1 mb-6" data-testid="help-article-title">{art.title}</h1>
                {art.excerpt && (
                    <p className="text-ink-600 italic border-l-2 border-ink-300 pl-4 mb-8" data-testid="help-article-excerpt">
                        {art.excerpt}
                    </p>
                )}
                <Markdown source={art.body} className="text-ink leading-relaxed" />
                <div className="mt-10 pt-6 border-t border-ink-200 font-mono text-xs text-ink-600 flex flex-wrap gap-4 justify-between">
                    <span data-testid="help-article-views">{art.view_count} view{art.view_count === 1 ? "" : "s"}</span>
                    {art.published_at && (
                        <span data-testid="help-article-date">
                            Published {new Date(art.published_at).toLocaleDateString()}
                        </span>
                    )}
                </div>

                {related.length > 0 && (
                    <section className="mt-16" data-testid="help-related">
                        <p className="eyebrow mb-3">RELATED</p>
                        <ul className="grid sm:grid-cols-2 gap-3">
                            {related.map((r) => (
                                <li key={r.id}>
                                    <Link
                                        to={`/help/${r.slug}`}
                                        className="block p-4 border border-ink-200 hover:border-ink"
                                        data-testid={`help-related-${r.slug}`}
                                    >
                                        <span className="text-sm font-semibold">{r.title}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </article>
            <Footer />
        </div>
    );
}
